import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Package, PackagePlus, ArrowLeftRight, Undo2, SlidersHorizontal, CalendarCheck,
  Wallet, HandCoins, Receipt, BarChart3, ScrollText, Settings, LogOut, Menu, CalendarDays, Warehouse, Briefcase,
  ClipboardList, FileText, Truck, MapPin, CreditCard, Coins, BookOpen, ClipboardCheck,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";

export const ADMIN_NAV = [
  ["", "Dashboard", LayoutDashboard], ["salesmen", "Salesmen", Users], ["products", "Products", Package],
  ["issue", "Stock Issue", PackagePlus], ["transfers", "Stock Transfers", ArrowLeftRight], ["returns", "Stock Returns", Undo2],
  ["adjustments", "Stock Adjustments", SlidersHorizontal], ["closures", "Daily Closures", CalendarCheck],
  ["collections", "Collections", Wallet], ["receivables", "Salesman Receivables", HandCoins], ["expenses", "Expenses", Receipt],
  ["reports", "Reports", BarChart3], ["attendance", "Attendance", CalendarDays], ["audit", "Audit Logs", ScrollText], ["settings", "Settings", Settings],
  ["inventory", "Inventory & Warehouses", Warehouse, "V2 · Inventory, Wholesale & Credit"], ["inventory?tab=closing", "Day-End Stock Closing", ClipboardCheck],
  ["customers?tab=team", "Wholesale Management", Briefcase], ["wholesale?tab=orders", "Wholesale Orders", ClipboardList],
  ["wholesale?tab=bills", "Wholesale Bills", FileText], ["wholesale?tab=loading", "Loading Sheets", Truck],
  ["customers", "Customers & Beats", MapPin], ["credit", "FMCG Credit Bills", CreditCard], ["payments", "Payment Collections", Coins],
  ["credit?tab=outstanding", "Outstanding Receivables", HandCoins], ["cashbook", "Cashbook", BookOpen],
];

function Nav({ onNav }) {
  return (
    <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4">
      {ADMIN_NAV.map(([path, label, Icon, group]) => (
        <div key={path}>{group && <p className="mt-4 px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">{group}</p>}
        <NavLink to={`/admin/${path}`} end={path === "" || path.includes("?")} onClick={onNav} data-testid={`nav-${(path || "dashboard").replace(/[?=]/g, "-")}`}
          className={({ isActive }) => `flex items-center gap-3 rounded px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}>
          <Icon className="h-4 w-4" /> {label}
        </NavLink></div>
      ))}
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2 border-b border-white/10 px-5 py-5">
      <div className="flex h-8 w-8 items-center justify-center rounded bg-blue-600 font-mono text-sm font-bold text-white">S</div>
      <div><p className="font-bold leading-none text-white">SIMS</p><p className="text-[10px] uppercase tracking-wider text-slate-400">Stock Issue & Salesman Mgmt</p></div>
    </div>
  );
}

export default function AdminLayout() {
  const { user, settings, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const overridden = !!settings?.business_date_override;
  const side = (
    <div className="flex h-full flex-col bg-[#0F172A]">
      <Brand />
      <Nav onNav={() => setOpen(false)} />
      <div className="border-t border-white/10 p-4">
        <p className="text-sm font-semibold text-white">{user?.name}</p>
        <p className="text-xs text-slate-400">@{user?.username} · Admin</p>
        <button className="mt-3 flex items-center gap-2 text-xs text-slate-300 hover:text-white" onClick={() => { logout(); nav("/login"); }} data-testid="logout-button">
          <LogOut className="h-3.5 w-3.5" /> Log out
        </button>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 hidden w-[260px] lg:block">{side}</aside>
      <Sheet open={open} onOpenChange={setOpen}><SheetContent side="left" className="w-[260px] border-0 p-0">{side}</SheetContent></Sheet>
      <div className="lg:pl-[260px]">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/80 px-4 backdrop-blur-md lg:px-8">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button>
          <p className="text-sm font-semibold text-slate-700">Admin Portal</p>
          <div className={`ml-auto flex items-center gap-2 rounded border px-3 py-1 text-xs font-semibold ${overridden ? "border-amber-300 bg-amber-50 text-amber-800" : "border-slate-200 bg-white text-slate-700"}`} data-testid="business-date-badge">
            <CalendarDays className="h-3.5 w-3.5" /> Business date: {fmtDate(settings?.business_date)} {overridden && "(override)"}
          </div>
        </header>
        <main className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
